class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();

    // Cart Recommendation
    this.cartRecommendation();
  }

  connectedCallback() {
    // Only attach pageshow listener once per page
    if (!CartDrawer.bfcacheListenerAttached) {
      CartDrawer.bfcacheListenerAttached = true;
      this.handleBfcache();
    }
  }

  handleBfcache() {
    window.addEventListener('pageshow', (event) => {
      // Check if page was restored from bfcache
      if (event.persisted) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        // Define sections to fetch - hardcoded to avoid method call issues
        const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble'];
        const sectionsUrl = `${window.location.pathname}?sections=${sectionsToFetch.join(',')}`;

        fetch(sectionsUrl)
          .then(response => response.json())
          .then(sections => {
            // Update cart drawer content
            const cartDrawerSection = sections['cart-drawer'];
            if (cartDrawerSection) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(cartDrawerSection, 'text/html');
              const freshInner = doc.querySelector('.drawer__inner');
              const currentInner = cartDrawer.querySelector('.drawer__inner');

              if (currentInner && freshInner) {
                currentInner.innerHTML = freshInner.innerHTML;
              }

              // Update is-empty class
              const freshCartDrawer = doc.querySelector('cart-drawer');
              if (freshCartDrawer) {
                if (freshCartDrawer.classList.contains('is-empty')) {
                  cartDrawer.classList.add('is-empty');
                } else {
                  cartDrawer.classList.remove('is-empty');
                }
              }
            }

            // Update cart icon bubble
            const cartIconSection = sections['cart-icon-bubble'];
            if (cartIconSection) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(cartIconSection, 'text/html');
              const freshIcon = doc.querySelector('.shopify-section');
              const currentIcon = document.querySelector('#cart-icon-bubble');
              if (currentIcon && freshIcon) {
                currentIcon.innerHTML = freshIcon.innerHTML;
              }
            }

            // Re-initialize cart recommendation carousel
            if (cartDrawer.cartRecommendation) {
              cartDrawer.cartRecommendation();
            }
          })
          .catch(error => {
            console.error('Error refreshing cart state:', error);
          });
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");

    if(document.querySelector('.snize-ac-results') && document.querySelector('.snize-ac-results').classList.contains('snize-ac-results--visible')) {
      document.querySelector('.snize-ac-results').classList.remove('snize-ac-results--visible');
    }

    if(document.querySelector('.search__modal') && !document.querySelector('.search__modal').classList.contains('search__modal--hidden')) {
      document.querySelector('.search__modal').classList.add('search__modal--hidden');
    }

    const mobileSearchTrigger = document.querySelector(".search__button--mobile");
    const mobileSearch = document.querySelector(".search__content--mobile");

    if(mobileSearchTrigger && mobileSearch && mobileSearch.classList.contains('open')){
      mobileSearchTrigger.click();
    }
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, dontOpen) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      if (!dontOpen) {
        this.open();
      }
    });

    // Re-render cart recommendation carousel after drawer content is updated
    this.cartRecommendation();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  cartRecommendation() {
    const cartRecommendationContainer = this.querySelector('.js-cart-recommendation');
    if (!cartRecommendationContainer) return;

    const perView = cartRecommendationContainer.getAttribute('data-per-view') || 3;
    const slider = new Swiper(cartRecommendationContainer, {
      slidesPerView: perView ? parseInt(perView) : 3,
      slidesPerGroup: perView ? parseInt(perView) : 3,
      spaceBetween: 8,
      speed: 1000,
    });

    this.handleQuickAdd();
  }

  handleQuickAdd() {
    const cartRecommendationContainer = this.querySelector('.js-cart-recommendation');
    if (!cartRecommendationContainer) return;
    const buttons = cartRecommendationContainer.querySelectorAll(".product-card__quick-add-btn");
    if (buttons.length === 0) return;

    // Add event listener to each button
    buttons.forEach((button) => {
      button.addEventListener("click", function () {
        const id = this.getAttribute("data-id");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        formData.append("id", id);

        const currentCart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        if (currentCart) {
          formData.append(
            "sections",
            currentCart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          currentCart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        button.disabled = true;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });
              return;
            } else if (!currentCart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
            });

            currentCart.renderContents(response, true);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            button.disabled = false;
          });
      });
    });
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
